import {
  MAX_OWNER_GOOD_FOR,
  PLACE_GOOD_FOR_OCCASIONS,
  type PlaceGoodForOccasion,
} from '@pulsify/shared/dto'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useListingGoodFor,
  useUpdateListingGoodFor,
} from '~/entities/listing-good-for/model/use-listing-good-for'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { Skeleton } from '~/shared/ui/skeleton'

const OCCASION_LABELS: Record<PlaceGoodForOccasion, () => string> = {
  date: m.goodForDate,
  business: m.goodForBusiness,
  study: m.goodForStudy,
  family: m.goodForFamily,
  friends: m.goodForFriends,
  party: m.goodForParty,
  coffee: m.goodForCoffee,
}

/** Up to three occasions the place names for itself, and the labels its guests published. */
export function GoodForCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const goodFor = useListingGoodFor(businessId, listingId)
  const update = useUpdateListingGoodFor(businessId, listingId)

  if (goodFor.isError) {
    return (
      <Card className="flex flex-col items-start gap-3 p-4">
        <h3 className="font-semibold font-serif">{m.goodForTitle()}</h3>
        <p className="text-muted-foreground text-sm">{m.goodForLoadError()}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => goodFor.refetch()}>
          {m.tryAgain()}
        </Button>
      </Card>
    )
  }
  if (!goodFor.data) return <Skeleton className="h-56 w-full rounded-xl" />

  const { chosen, guests } = goodFor.data
  const full = chosen.length >= MAX_OWNER_GOOD_FOR
  const published = new Set(guests.map((guest) => guest.occasion))
  const pending = PLACE_GOOD_FOR_OCCASIONS.filter((occasion) => !published.has(occasion))

  async function toggle(occasion: PlaceGoodForOccasion) {
    const next = chosen.includes(occasion)
      ? chosen.filter((current) => current !== occasion)
      : [...chosen, occasion]
    try {
      await update.mutateAsync(next)
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold font-serif">{m.goodForTitle()}</h3>
        <InfoTooltip label={m.goodForTitle()}>{m.goodForInfo()}</InfoTooltip>
        <span className="ml-auto text-muted-foreground text-xs tabular-nums">
          {m.goodForCount({ count: chosen.length, max: MAX_OWNER_GOOD_FOR })}
        </span>
      </div>
      <p className="text-muted-foreground text-sm">{m.goodForOwnHint()}</p>
      <fieldset className="flex flex-wrap gap-2" aria-label={m.goodForTitle()}>
        {PLACE_GOOD_FOR_OCCASIONS.map((occasion) => {
          const selected = chosen.includes(occasion)
          return (
            <Button
              key={occasion}
              type="button"
              size="sm"
              variant={selected ? 'default' : 'outline'}
              aria-pressed={selected}
              disabled={!selected && full}
              onClick={() => toggle(occasion)}
            >
              {OCCASION_LABELS[occasion]()}
            </Button>
          )
        })}
      </fieldset>

      <div className="border-t pt-3">
        <h4 className="font-medium text-sm">{m.goodForGuestsTitle()}</h4>
        {guests.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1.5">
            {guests.map(({ occasion, voters }) => (
              <li key={occasion} className="flex items-baseline justify-between gap-3 text-sm">
                <span>{OCCASION_LABELS[occasion]()}</span>
                <span className="text-muted-foreground tabular-nums">
                  {m.goodForGuestsVoters({ count: voters })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {pending.length > 0 && (
          <p className="mt-2 text-muted-foreground text-sm">
            {guests.length === 0
              ? m.goodForGuestsNoneYet()
              : m.goodForGuestsPending({
                  occasions: pending.map((occasion) => OCCASION_LABELS[occasion]()).join(', '),
                })}
          </p>
        )}
      </div>
    </Card>
  )
}
